<template>
  <div class="container">
    <el-form
      :model="form"
      label-width="120px"
      @submit.prevent="submitForm"
      class="project-form"
    >
      <div class="row">
        <div class="col-md-8">
          <!-- Basic Info -->
          <div class="card mb-4">
            <div class="card-body">
              <h3>Basic Information</h3>

              <el-form-item
                label="Name *"
                :error="errors.name"
                :class="{ 'is-error': errors.name }"
              >
                <el-input v-model="form.name" required></el-input>
                <small class="text-muted">Required</small>
              </el-form-item>

              <el-form-item
                label="Tag Line"
                :error="errors.tag_line"
                :class="{ 'is-error': errors.tag_line }"
              >
                <el-input v-model="form.tag_line"></el-input>
              </el-form-item>

              <el-form-item
                label="Short Description"
                :error="errors.description_short"
                :class="{ 'is-error': errors.description_short }"
              >
                <el-input type="textarea" v-model="form.description_short" :rows="3"></el-input>
              </el-form-item>

              <el-form-item
                label="Content"
                :error="errors.content"
                :class="{ 'is-error': errors.content }"
              >
                <rich-text-editor v-model="form.content"></rich-text-editor>
              </el-form-item>
            </div>
          </div>

          <!-- Links -->
          <div class="card mb-4">
            <div class="card-body">
              <h3>Links</h3>

              <el-form-item
                label="Logo URL"
                :error="errors.logo"
                :class="{ 'is-error': errors.logo }"
              >
                <el-input v-model="form.logo"></el-input>
              </el-form-item>

              <el-form-item
                label="Website"
                :error="errors.url_main"
                :class="{ 'is-error': errors.url_main }"
              >
                <el-input v-model="form.url_main"></el-input>
              </el-form-item>

              <el-form-item
                label="Twitter"
                :error="errors['social_links.twitter_url']"
                :class="{ 'is-error': errors['social_links.twitter_url'] }"
              >
                <el-input v-model="form.social_links.twitter_url"></el-input>
              </el-form-item>

              <el-form-item
                label="Discord"
                :error="errors['social_links.discord_url']"
                :class="{ 'is-error': errors['social_links.discord_url'] }"
              >
                <el-input v-model="form.social_links.discord_url"></el-input>
              </el-form-item>

              <el-form-item
                label="Reddit"
                :error="errors['social_links.reddit_url']"
                :class="{ 'is-error': errors['social_links.reddit_url'] }"
              >
                <el-input v-model="form.social_links.reddit_url"></el-input>
              </el-form-item>

              <el-form-item
                label="GitHub"
                :error="errors['social_links.github_url']"
                :class="{ 'is-error': errors['social_links.github_url'] }"
              >
                <el-input v-model="form.social_links.github_url"></el-input>
              </el-form-item>

              <el-form-item
                label="Blog"
                :error="errors.url_blog"
                :class="{ 'is-error': errors.url_blog }"
              >
                <el-input v-model="form.url_blog"></el-input>
              </el-form-item>

              <el-form-item
                label="Documentation"
                :error="errors.url_documentation"
                :class="{ 'is-error': errors.url_documentation }"
              >
                <el-input v-model="form.url_documentation"></el-input>
              </el-form-item>

              <el-form-item
                label="Whitepaper"
                :error="errors.url_whitepaper"
                :class="{ 'is-error': errors.url_whitepaper }"
              >
                <el-input v-model="form.url_whitepaper"></el-input>
              </el-form-item>
            </div>
          </div>
        </div>

        <div class="col-md-4">
          <!-- Categories -->
          <div class="card mb-4">
            <div class="card-body">
              <h3>Categories</h3>
              <el-form-item>
                <el-select
                  v-model="form.categories"
                  multiple
                  filterable
                  placeholder="Select categories"
                >
                  <el-option
                    v-for="category in categories"
                    :key="category.id"
                    :label="category.name"
                    :value="category.id"
                  ></el-option>
                </el-select>
              </el-form-item>
            </div>
          </div>

          <!-- Assets -->
          <div class="card mb-4">
            <div class="card-body">
              <h3>Assets</h3>
              <div v-for="(asset, index) in form.assets" :key="index" class="mb-3">
                <div class="d-flex justify-content-between align-items-center mb-2">
                  <h5 class="mb-0">Asset {{ index + 1 }}</h5>
                  <el-button type="danger" size="small" @click="removeAsset(index)">Remove</el-button>
                </div>
                <el-form-item label="Name *">
                  <el-input v-model="asset.name" required></el-input>
                  <small class="text-muted">Required</small>
                </el-form-item>
                <el-form-item label="Ticker *">
                  <el-input v-model="asset.ticker" required></el-input>
                  <small class="text-muted">Required</small>
                </el-form-item>
                <el-form-item label="Description">
                  <el-input type="textarea" v-model="asset.short_description" :rows="2"></el-input>
                </el-form-item>
                <el-form-item label="Documentation URL">
                  <el-input v-model="asset.url_to_asset_docs"></el-input>
                </el-form-item>
                <el-form-item label="Icon URL">
                  <el-input v-model="asset.icon"></el-input>
                </el-form-item>
                <el-form-item label="Token Address">
                  <el-input v-model="asset.address"></el-input>
                </el-form-item>
              </div>
              <el-button type="primary" @click="addAsset">Add Asset</el-button>
            </div>
          </div>

          <!-- Products -->
          <div class="card mb-4">
            <div class="card-body">
              <h3>Products</h3>
              <div v-for="(product, index) in form.products" :key="index" class="mb-3">
                <div class="d-flex justify-content-between align-items-center mb-2">
                  <h5 class="mb-0">Product {{ index + 1 }}</h5>
                  <el-button type="danger" size="small" @click="removeProduct(index)">Remove</el-button>
                </div>
                <el-form-item label="Name *">
                  <el-input v-model="product.name" required></el-input>
                  <small class="text-muted">Required</small>
                </el-form-item>
                <el-form-item label="Description">
                  <el-input type="textarea" v-model="product.description_short" :rows="2"></el-input>
                </el-form-item>
                <el-form-item label="URL">
                  <el-input v-model="product.url_to_product"></el-input>
                </el-form-item>
                <el-form-item label="Launch Date *">
                  <el-date-picker v-model="product.launch_date" type="date" required></el-date-picker>
                  <small class="text-muted">Required</small>
                </el-form-item>
                <el-form-item label="Program Address">
                  <el-input v-model="product.product_address"></el-input>
                </el-form-item>
              </div>
              <el-button type="primary" @click="addProduct">Add Product</el-button>
            </div>
          </div>
        </div>
      </div>

      <div class="d-flex justify-content-between mt-4">
        <div>
          <el-button @click="$router.back()">Cancel</el-button>
          <el-button
            v-if="isEdit"
            type="danger"
            @click="confirmDelete"
            :loading="deleteLoading"
          >
            Delete Project
          </el-button>
        </div>
        <el-button type="primary" native-type="submit" :loading="loading">
          {{ isEdit ? 'Update Project' : 'Create Project' }}
        </el-button>
      </div>
    </el-form>
  </div>
</template>

<script>
import RichTextEditor from './RichTextEditor.vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import 'element-plus/theme-chalk/el-message-box.css'

export default {
  components: {
    RichTextEditor
  },

  props: {
    initialData: {
      type: Object,
      default: () => ({})
    },
    submitUrl: {
      type: String,
      required: true
    },
    isEdit: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      loading: false,
      deleteLoading: false,
      categories: [],
      errors: {},
      form: {
        name: '',
        tag_line: '',
        description_short: '',
        content: '',
        logo: '',
        url_main: '',
        social_links: {
          twitter_url: null,
          discord_url: null,
          reddit_url: null,
          github_url: null
        },
        url_blog: '',
        url_documentation: '',
        url_whitepaper: '',
        categories: [],
        assets: [],
        products: []
      }
    }
  },

  async created() {
    await this.loadCategories()
    if (this.isEdit) {
      this.initializeFormData()
    }
  },

  methods: {
    async loadCategories() {
      try {
        const response = await fetch('/api/categories')
        this.categories = await response.json()
      } catch (error) {
        console.error('Error loading categories:', error)
        ElMessage.error('Failed to load categories')
      }
    },

    initializeFormData() {
      Object.keys(this.form).forEach(key => {
        if (this.initialData[key] !== undefined) {
          if (key === 'assets' || key === 'products') {
            this.form[key] = [...this.initialData[key]]
          } else {
            this.form[key] = this.initialData[key]
          }
        }
      })

      if (this.initialData.categories) {
        this.form.categories = this.initialData.categories.map(c => c.id)
      }
    },

    addAsset() {
      this.form.assets.push({
        name: '',
        ticker: '',
        short_description: '',
        address: ''
      })
    },

    async removeAsset(index) {
      try {
        console.log('Opening confirmation dialog...');
        await ElMessageBox.confirm(
          'Are you sure you want to remove this asset?',
          'Warning',
          {
            confirmButtonText: 'Yes',
            cancelButtonText: 'No',
            type: 'warning',
            closeOnClickModal: false
          }
        )
        console.log('User confirmed removal');
        this.form.assets.splice(index, 1)
      } catch {
        // User cancelled
      }
    },

    addProduct() {
      this.form.products.push({
        name: '',
        description_short: '',
        url_to_product: '',
        launch_date: null
      })
    },

    async removeProduct(index) {
      try {
        console.log('Opening confirmation dialog...');
        await ElMessageBox.confirm(
          'Are you sure you want to remove this product?',
          'Warning',
          {
            confirmButtonText: 'Yes',
            cancelButtonText: 'No',
            type: 'warning',
            closeOnClickModal: false
          }
        )
        console.log('User confirmed removal');
        this.form.products.splice(index, 1)
      } catch {
        // User cancelled
      }
    },

    formatDates(data) {
      // Deep clone the data to avoid modifying the original
      const formatted = JSON.parse(JSON.stringify(data));
      
      if (formatted.products) {
        formatted.products.forEach(product => {
          if (product.launch_date) {
            // Format as YYYY-MM-DD
            product.launch_date = new Date(product.launch_date).toISOString().split('T')[0];
          }
        });
      }
      return formatted;
    },

    async submitForm() {
      this.loading = true
      this.errors = {}
      try {
        const formattedData = this.formatDates(this.form);
        const response = await axios({
          method: this.isEdit ? 'put' : 'post',
          url: this.submitUrl.replace('/edit', ''),
          data: formattedData,
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]').content
          }
        })

        if (response.data.success) {
          ElMessage.success(response.data.message)
          window.location.href = `/projects/${response.data.project.slug}`
        } else {
          throw new Error(response.data.message || 'Failed to save project')
        }
      } catch (error) {
        console.error('Error saving project:', error)
        
        if (error.response) {
          // The request was made and the server responded with a status code
          // that falls out of the range of 2xx
          const responseData = error.response.data
          if (responseData.errors) {
            // Handle validation errors
            const firstErrorKey = Object.keys(responseData.errors)[0]
            const firstError = responseData.errors[firstErrorKey][0]
            ElMessage.error(firstError)
            this.errors = responseData.errors
          } else if (responseData.error) {
            // Handle specific error message
            ElMessage.error(responseData.error)
          } else {
            // Handle generic error message
            ElMessage.error(responseData.message || 'Failed to save project')
          }
        } else if (error.request) {
          // The request was made but no response was received
          ElMessage.error('Network error - no response received')
        } else {
          // Something happened in setting up the request that triggered an Error
          ElMessage.error(error.message || 'An unexpected error occurred')
        }
      } finally {
        this.loading = false
      }
    },

    async confirmDelete() {
      try {
        await ElMessageBox.confirm(
          'Are you sure you want to delete this project? This action cannot be undone.',
          'Warning',
          {
            confirmButtonText: 'Yes, Delete',
            cancelButtonText: 'Cancel',
            type: 'warning',
            closeOnClickModal: false
          }
        )

        this.deleteLoading = true
        const response = await fetch(`/projects/${this.initialData.id}`, {
          method: 'DELETE',
          headers: {
            'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]').content
          }
        })

        if (!response.ok) {
          throw new Error('Failed to delete project')
        }

        ElMessage.success('Project deleted successfully')
        window.location.href = '/projects'
      } catch (error) {
        if (error.message !== 'ElMessageBox was closed') {
          console.error('Error deleting project:', error)
          ElMessage.error(error.message || 'Failed to delete project')
        }
      } finally {
        this.deleteLoading = false
      }
    }
  }
}
</script>
